import { useEffect } from 'react';
import type { FormBuilderHandle } from '@kula-ai/components';
import { requiresNoteTakerEnabled } from '@/formConfigs/useNoteTakerConfig';
import type { ScheduleInterviewPayload } from '../../../config/types';

interface UseNoteTakerMandateHealParams {
  /** The FormBuilder imperative handle (getValues / setValue). */
  formRef: React.RefObject<FormBuilderHandle | null>;
  /** Mandate flag — MUST be the same expression the schema uses for the
   *  note-taker field's `required`/`disabled` so the flag and the value
   *  can't diverge. */
  isMandatory: boolean;
  /** LIVE form location (mirrored from the form-level onChange, which fires
   *  on programmatic `setValue` too). */
  location: string;
  /** LIVE participant_ids. The note-taker tile is gated on `participant_ids
   *  isNotEmpty AND location virtual`, and FormBuilder's ConditionalField
   *  restores its pre-hide snapshot when the tile reappears — clobbering a
   *  mandated `true`. Keyed here so the heal re-asserts after that restore
   *  (child effects run before this parent effect). */
  participantIds?: string;
  payloadRef: React.MutableRefObject<ScheduleInterviewPayload>;
}

/**
 * Keeps `enable_note_taker: true` whenever the AI-notetaker is mandated for a
 * virtual location.
 *
 * When the notetaker is mandated the toggle renders `disabled` + `required`,
 * so the form value MUST be `true` — otherwise the Details step fails
 * validation with "This field is required" on a switch the user can't toggle
 * on. The value is seeded on at hydrate, but two paths reach the toggle while
 * it is still `false`:
 *   1. adhoc mounts BEFORE its plan (hence the fresh mandate) resolves;
 *   2. the location is set to a virtual platform via a programmatic
 *      `setValue` (adhoc auto-seed, Zoom→Meet fallback) — which never reaches
 *      the `sharedState.location`-keyed recovery in DetailsStep.
 *
 * Keyed on the LIVE form `location`, so it re-asserts whichever of
 * {mandate, virtual location} lands last. Idempotent — a no-op once the value
 * is already on — and it only ever turns the toggle ON, and only in the
 * mandated + virtual state, so it can never override a user choice (the toggle
 * is disabled then) or touch a non-mandated interview.
 */
export function useNoteTakerMandateHeal({
  formRef,
  isMandatory,
  location,
  participantIds,
  payloadRef,
}: UseNoteTakerMandateHealParams): void {
  useEffect(() => {
    if (!requiresNoteTakerEnabled(isMandatory, location)) return;
    const form = formRef.current;
    if (!form) return;
    const values = form.getValues() as Record<string, unknown> | undefined;
    if (values?.enable_note_taker === true) return;
    form.setValue('enable_note_taker', true);
    payloadRef.current.details.enable_note_taker = true;
  }, [isMandatory, location, participantIds, formRef, payloadRef]);
}
